import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { usePortal } from './PortalContext';

const PortalInvoicesList = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();
  const { isAuthenticated, fetchInvoices, logout } = usePortal();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/portal/login');
      return;
    }

    loadInvoices();
  }, [isAuthenticated, navigate]);

  const loadInvoices = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchInvoices();
      setInvoices(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return '#22c55e';
      case 'overdue':
        return '#ef4444';
      case 'pending':
      case 'sent':
        return '#f59e0b';
      default:
        return '#6b7280';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredInvoices = invoices.filter(inv => {
    if (filter === 'all') return true;
    if (filter === 'unpaid') return inv.status !== 'paid';
    if (filter === 'paid') return inv.status === 'paid';
    return true;
  });

  const unpaidTotal = invoices
    .filter(inv => inv.status !== 'paid')
    .reduce((sum, inv) => sum + parseFloat(inv.total || inv.amount || 0), 0);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div style={styles.container}>
      {/* Navigation */}
      <nav style={styles.navbar}>
        <div style={styles.navContent}>
          <div style={styles.navLeft}>
            <div style={styles.navLogo}>🔧</div>
            <h1 style={styles.navTitle}>Customer Portal</h1>
          </div>
          <button onClick={handleLogout} style={styles.logoutButton}>
            Logout
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main style={styles.main}>
        <div style={styles.content}>
          {/* Breadcrumb */}
          <div style={styles.breadcrumb}>
            <Link to="/portal/dashboard" style={styles.breadcrumbLink}>Dashboard</Link>
            <span style={styles.breadcrumbSeparator}>/</span>
            <span style={styles.breadcrumbCurrent}>Invoices</span>
          </div>

          {/* Header */}
          <div style={styles.header}>
            <div>
              <h2 style={styles.title}>All Invoices</h2>
              <p style={styles.subtitle}>
                {invoices.length} total · {formatCurrency(unpaidTotal)} outstanding
              </p>
            </div>
          </div>

          {error && (
            <div style={styles.errorBox}>
              <span>⚠️</span> {error}
            </div>
          )}

          {/* Filter Tabs */}
          <div style={styles.filterTabs}>
            <button
              onClick={() => setFilter('all')}
              style={{
                ...styles.filterTab,
                ...(filter === 'all' ? styles.filterTabActive : {})
              }}
            >
              All
            </button>
            <button
              onClick={() => setFilter('unpaid')}
              style={{
                ...styles.filterTab,
                ...(filter === 'unpaid' ? styles.filterTabActive : {})
              }}
            >
              Unpaid
            </button>
            <button
              onClick={() => setFilter('paid')}
              style={{
                ...styles.filterTab,
                ...(filter === 'paid' ? styles.filterTabActive : {})
              }}
            >
              Paid
            </button>
          </div>

          {/* Invoices List */}
          <div style={styles.card}>
            {loading ? (
              <div style={styles.loadingState}>
                <div style={styles.spinner}></div>
                <p>Loading invoices...</p>
              </div>
            ) : filteredInvoices.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>📄</div>
                <h3 style={styles.emptyTitle}>No invoices found</h3>
                <p style={styles.emptyText}>
                  {filter !== 'all'
                    ? `No ${filter} invoices found.`
                    : "You don't have any invoices yet."}
                </p>
                {filter !== 'all' && (
                  <button
                    onClick={() => setFilter('all')}
                    style={styles.emptyButton}
                  >
                    View all invoices
                  </button>
                )}
              </div>
            ) : (
              <div style={styles.table}>
                <div style={styles.tableHeader}>
                  <span style={styles.invoiceCol}>Invoice</span>
                  <span style={styles.dateCol}>Date</span>
                  <span style={styles.statusCol}>Status</span>
                  <span style={styles.amountCol}>Amount</span>
                  <span style={styles.actionCol}></span>
                </div>
                {filteredInvoices.map((invoice) => (
                  <div key={invoice.id} style={styles.tableRow}>
                    <div style={styles.invoiceCol}>
                      <p style={styles.invoiceNumber}>{invoice.invoice_number}</p>
                      <p style={styles.invoiceCustomer}>{invoice.customer_name}</p>
                    </div>
                    <span style={styles.dateCol}>
                      {formatDate(invoice.date)}
                    </span>
                    <span style={styles.statusCol}>
                      <span style={{
                        ...styles.statusBadge,
                        background: `${getStatusColor(invoice.status)}20`,
                        color: getStatusColor(invoice.status)
                      }}>
                        {invoice.status === 'paid' ? '✓' :
                         invoice.status === 'overdue' ? '⚠' :
                         invoice.status === 'pending' ? '⏳' : '•'} {' '}
                        {invoice.status}
                      </span>
                    </span>
                    <span style={styles.amountCol}>
                      {formatCurrency(invoice.total || invoice.amount)}
                    </span>
                    <div style={styles.actionCol}>
                      <Link
                        to={`/portal/invoices/${invoice.id}`}
                        style={styles.viewLink}
                      >
                        View
                      </Link>
                      {invoice.status !== 'paid' && (
                        <Link
                          to={`/portal/invoices/${invoice.id}?pay=true`}
                          style={styles.payLink}
                        >
                          Pay
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Back Link */}
          <Link to="/portal/dashboard" style={styles.backLink}>
            ← Back to Dashboard
          </Link>
        </div>
      </main>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f3f4f6',
    display: 'flex',
    flexDirection: 'column'
  },
  navbar: {
    background: 'white',
    borderBottom: '1px solid #e5e7eb',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    width: '100%'
  },
  navContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%'
  },
  navLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  navLogo: {
    fontSize: '24px'
  },
  navTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: 0
  },
  logoutButton: {
    background: 'transparent',
    border: '1px solid #d1d5db',
    color: '#6b7280',
    padding: '8px 16px',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  main: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    minHeight: 'calc(100vh - 73px)',
    padding: '32px 24px'
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    width: '100%',
    maxWidth: '1000px'
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px'
  },
  breadcrumbLink: {
    color: '#6b7280',
    textDecoration: 'none'
  },
  breadcrumbSeparator: {
    color: '#d1d5db'
  },
  breadcrumbCurrent: {
    color: '#111827',
    fontWeight: '500'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#111827',
    margin: '0 0 4px 0'
  },
  subtitle: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0
  },
  errorBox: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    padding: '12px 16px',
    color: '#dc2626',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  filterTabs: {
    display: 'flex',
    gap: '8px'
  },
  filterTab: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    background: 'transparent',
    color: '#6b7280',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  filterTabActive: {
    background: 'white',
    color: '#111827',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflow: 'hidden'
  },
  loadingState: {
    padding: '60px',
    textAlign: 'center',
    color: '#6b7280'
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #e5e7eb',
    borderTopColor: '#f97316',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto 16px'
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center'
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px'
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 8px 0'
  },
  emptyText: {
    color: '#6b7280',
    fontSize: '14px',
    margin: '0 0 16px 0'
  },
  emptyButton: {
    color: '#f97316',
    background: 'transparent',
    border: 'none',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    textDecoration: 'underline'
  },
  table: {
    display: 'flex',
    flexDirection: 'column'
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr 120px',
    gap: '16px',
    padding: '16px 24px',
    background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    fontSize: '12px',
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr 120px',
    gap: '16px',
    padding: '16px 24px',
    borderBottom: '1px solid #f3f4f6',
    alignItems: 'center',
    transition: 'background 0.2s',
    ':hover': {
      background: '#f9fafb'
    }
  },
  invoiceCol: {
    display: 'flex',
    flexDirection: 'column'
  },
  invoiceNumber: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 2px 0'
  },
  invoiceCustomer: {
    fontSize: '13px',
    color: '#6b7280',
    margin: 0
  },
  dateCol: {
    fontSize: '14px',
    color: '#6b7280'
  },
  statusCol: {},
  statusBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: '500'
  },
  amountCol: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#111827'
  },
  actionCol: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end'
  },
  viewLink: {
    color: '#f97316',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
    padding: '4px 8px'
  },
  payLink: {
    background: '#f97316',
    color: 'white',
    textDecoration: 'none',
    fontSize: '13px',
    fontWeight: '500',
    padding: '6px 12px',
    borderRadius: '6px'
  },
  backLink: {
    color: '#6b7280',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500'
  }
};

export default PortalInvoicesList;
